(() => {
  const section = document.querySelector('[data-scroll-video]');
  if (!section) return;

  const track = section.querySelector('.svideo__track');
  const overlay = section.querySelector('[data-svideo-overlay]');
  const video = section.querySelector('.svideo__video') || section.querySelector('video');
  if (!track || !video) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const OVERLAY_START = 0.78;

  // How fast the displayed progress chases raw scroll position. Higher = snappier,
  // lower = smoother. Framerate-independent (see the exponential below).
  const SMOOTHING = 16;
  // Below this progress delta the scrub has settled; stop issuing seeks.
  const SETTLE_EPS = 0.0004;

  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  let duration = 0;
  let frameRate = 24;
  let lastSeekTime = -1;
  let seeking = false;
  let seekStarted = 0;
  let ready = false;
  let unlocked = false;
  let active = false;
  let complete = false;
  let revealing = false;
  let loopId = null;
  let lastFrameStamp = 0;
  let blobUrl = null;
  let blobTried = false;

  // Raw scroll position vs. the smoothed value we actually render/seek to.
  let targetP = 0;
  let shownP = 0;
  let primed = false;

  // Last values written to CSS, so we only touch style when something changed.
  let cssProgress = -1;
  let cssOverlay = -1;

  function readTarget() {
    const rect = track.getBoundingClientRect();
    const range = rect.height - window.innerHeight;
    const p = range > 0 ? clamp01(-rect.top / range) : 0;
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    return { p, inView };
  }

  function overlayProgress(p) {
    if (reduceMotion.matches) return 1;
    return clamp01((p - OVERLAY_START) / (1 - OVERLAY_START));
  }

  function seekTo(p) {
    if (!ready || !duration || reduceMotion.matches) return;

    const maxTime = Math.max(0, duration - 1 / frameRate);
    // Snap to a frame boundary so we never ask the decoder for a time that
    // rounds back to the frame already on screen.
    const step = 1 / frameRate;
    const time = Math.min(maxTime, Math.max(0, Math.round((p * maxTime) / step) * step));

    if (Math.abs(time - lastSeekTime) < step * 0.5) return;

    // Watchdog: if a seek never resolved (dropped `seeked`, decoder hiccup),
    // don't stay wedged forever.
    if (seeking && performance.now() - seekStarted < 400) return;

    lastSeekTime = time;
    seeking = true;
    seekStarted = performance.now();

    try {
      // Deliberately NOT fastSeek(): the asset is all-intra, so an exact seek
      // costs the same as a keyframe seek but lands on the right frame.
      video.currentTime = time;
    } catch (_) {
      seeking = false;
    }
  }

  function setActive(nextActive) {
    if (nextActive === active) return;
    active = nextActive;
    section.classList.toggle('is-active', active);
  }

  function render(p) {
    const overlayP = overlayProgress(p);

    const pRounded = Math.round(p * 2000) / 2000;
    if (pRounded !== cssProgress) {
      cssProgress = pRounded;
      section.style.setProperty('--svideo-progress', pRounded);
    }

    const oRounded = Math.round(overlayP * 2000) / 2000;
    if (oRounded !== cssOverlay) {
      cssOverlay = oRounded;
      section.style.setProperty('--svideo-overlay', oRounded);
    }

    const isRevealing = overlayP > 0;
    if (isRevealing !== revealing) {
      revealing = isRevealing;
      section.classList.toggle('is-revealing-cta', revealing);
      if (overlay) overlay.setAttribute('aria-hidden', revealing ? 'false' : 'true');
    }

    if (overlay) overlay.classList.toggle('is-interactive', overlayP > 0.55);

    const isComplete = p > 0.92;
    if (isComplete !== complete) {
      complete = isComplete;
      section.classList.toggle('is-complete', complete);
    }
  }

  function tick(stamp) {
    const { p, inView } = readTarget();
    targetP = p;
    setActive(inView);

    const dt = lastFrameStamp ? Math.min((stamp - lastFrameStamp) / 1000, 0.1) : 0;
    lastFrameStamp = stamp;

    if (!primed) {
      shownP = targetP;
      primed = true;
    } else {
      // Framerate-independent exponential approach — same feel at 60Hz and 120Hz.
      shownP += (targetP - shownP) * (1 - Math.exp(-SMOOTHING * dt));
    }

    const settled = Math.abs(targetP - shownP) < SETTLE_EPS;
    if (settled) shownP = targetP;

    render(shownP);

    if (inView && ready && duration && !reduceMotion.matches) seekTo(shownP);

    if (inView || !settled) {
      loopId = requestAnimationFrame(tick);
    } else {
      loopId = null;
      lastFrameStamp = 0;
    }
  }

  function start() {
    if (loopId) return;
    lastFrameStamp = 0;
    loopId = requestAnimationFrame(tick);
  }

  function onScroll() {
    if (!unlocked) unlock();
    start();
  }

  function onResize() {
    // Track height changed — jump rather than glide to the new mapping.
    primed = false;
    start();
  }

  function onMeta() {
    duration = video.duration || 0;
    ready = duration > 0 && Number.isFinite(duration);

    const rate = Number.parseFloat(video.getAttribute('data-frame-rate') || '');
    if (Number.isFinite(rate) && rate > 0) frameRate = rate;

    maybeFixSeekability();
    start();
  }

  function maybeFixSeekability() {
    if (blobTried || !ready || video.readyState < 2) return;

    const ranges = video.seekable;
    const seekableEnd = ranges.length ? ranges.end(ranges.length - 1) : 0;
    if (seekableEnd > 0.5) return; // Server honours range requests; nothing to do.

    blobTried = true;
    useBlobSource();
  }

  // Scrubbing requires a seekable resource. A server that doesn't answer HTTP
  // range requests with 206 (notably `shopify theme dev`) leaves the element
  // with an empty `seekable` range, so every currentTime write clamps to 0 and
  // the video appears frozen on frame one. Pulling the file down once and
  // handing the element a blob URL sidesteps that entirely, and has the side
  // benefit of guaranteeing no mid-scrub network stalls.
  async function useBlobSource() {
    const url = video.currentSrc;
    if (!url || url.startsWith('blob:')) return;

    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return;
      const blob = await res.blob();
      if (!blob.size) return;

      if (blobUrl) URL.revokeObjectURL(blobUrl);
      blobUrl = URL.createObjectURL(blob);

      // Re-run the unlock handshake against the new resource.
      unlocked = false;
      ready = false;
      seeking = false;
      lastSeekTime = -1;

      video.src = blobUrl;
      video.load();
      video.addEventListener('canplay', unlock, { once: true });
    } catch (_) {
      // Keep the direct source; scrubbing still works wherever ranges are served.
    }
  }

  async function unlock() {
    if (unlocked) return;
    unlocked = true;
    video.muted = true;
    video.playsInline = true;
    try {
      await video.play();
      video.pause();
    } catch (_) {
      // Scrubbing unlocks after the first scroll on stricter browsers.
    }
    seeking = false;
    lastSeekTime = -1;
    start();
  }

  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.removeAttribute('autoplay');

  video.addEventListener('loadedmetadata', onMeta);
  video.addEventListener('loadeddata', onMeta);
  video.addEventListener('durationchange', onMeta);
  video.addEventListener('canplay', maybeFixSeekability);
  video.addEventListener('seeked', () => {
    seeking = false;
  });

  window.addEventListener('pagehide', () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  });

  if (video.readyState >= 1) onMeta();

  if (reduceMotion.matches) {
    section.style.setProperty('--svideo-progress', '1');
    section.style.setProperty('--svideo-overlay', '1');
    section.classList.add('is-revealing-cta', 'is-complete');
    if (overlay) {
      overlay.classList.add('is-interactive');
      overlay.setAttribute('aria-hidden', 'false');
    }
    return;
  }

  if (video.readyState >= 2) unlock();
  else video.addEventListener('canplay', unlock, { once: true });

  start();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
})();
